import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Stack,
  IconButton,
  LinearProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon
} from '@mui/icons-material';

const ProfileInfoModal = ({ open, onClose, userProfile, onNavigateToTab }) => {
  const completionItems = [
    {
      key: 'salonName',
      label: 'Nom du salon',
      description: 'Ajoutez le nom de votre salon',
      completed: !!userProfile?.salonName,
      tabId: 0,
      section: 'profile'
    },
    {
      key: 'photo',
      label: 'Photo de profil',
      description: 'Ajoutez votre photo ou logo',
      completed: !!userProfile?.photo,
      tabId: 0,
      section: 'profile'
    },
    {
      key: 'photos',
      label: 'Photos du salon',
      description: 'Ajoutez des photos de votre salon',
      completed: userProfile?.photos?.length > 0,
      tabId: 0,
      section: 'photos'
    },
    {
      key: 'description',
      label: 'Description',
      description: 'Décrivez votre salon et vos services',
      completed: !!userProfile?.description,
      tabId: 0,
      section: 'profile'
    },
    {
      key: 'services',
      label: 'Services',
      description: 'Ajoutez les services que vous proposez',
      completed: (Array.isArray(userProfile?.services) && userProfile.services.length > 0) ||
                 (typeof userProfile?.services === 'string' && userProfile.services.trim().length > 0),
      tabId: 1,
      section: 'services'
    },
    {
      key: 'address',
      label: 'Adresse',
      description: 'Ajoutez l\'adresse de votre salon',
      completed: !!userProfile?.address,
      tabId: 0,
      section: 'profile'
    },
    {
      key: 'googleMyBusinessUrl',
      label: 'Google My Business',
      description: 'Ajoutez votre lien Google My Business pour afficher vos avis',
      completed: !!userProfile?.googleMyBusinessUrl,
      tabId: 0,
      section: 'profile'
    }
  ];

  const completionPercentage = Math.round(
    (completionItems.filter(item => item.completed).length / completionItems.length) * 100
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          fontWeight: 700, 
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Complétion de votre profil
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {completionPercentage}% complété
          </Typography>
        </Box>
        <IconButton 
          onClick={onClose}
          sx={{ 
            color: '#6b7280',
            '&:hover': { bgcolor: '#f3f4f6' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2, pb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: '#f3f4f6',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                bgcolor: completionPercentage === 100 ? '#22c55e' : '#3b82f6'
              }
            }}
          />
        </Box>
        
        <Stack spacing={3}>
          {completionItems.map((item) => (
            <Box
              key={item.key}
              onClick={() => !item.completed && onNavigateToTab && onNavigateToTab(item.tabId, item.section)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: item.completed ? '#f0fdf4' : '#fafaf9',
                border: `1px solid ${item.completed ? '#bbf7d0' : '#e5e7eb'}`,
                transition: 'all 0.2s ease',
                cursor: !item.completed ? 'pointer' : 'default',
                '&:hover': !item.completed ? {
                  bgcolor: '#f5f5f4',
                  borderColor: '#d6d3d1',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                } : {}
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: item.completed ? '#22c55e' : '#e5e7eb',
                  color: 'white',
                  flexShrink: 0,
                  mt: 0.25
                }}
              >
                {item.completed ? (
                  <CheckIcon sx={{ fontSize: 16 }} />
                ) : (
                  <Box 
                    sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      bgcolor: '#9ca3af' 
                    }} 
                  />
                )}
              </Box>
              
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: 600,
                    color: item.completed ? '#166534' : '#374151',
                    textDecoration: item.completed ? 'line-through' : 'none',
                    opacity: item.completed ? 0.8 : 1,
                    mb: 0.5
                  }}
                >
                  {item.label}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: item.completed ? '#16a34a' : '#6b7280',
                    fontSize: '0.875rem'
                  }}
                >
                  {item.completed ? 'Complété ✓' : item.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>

        {completionPercentage === 100 && (
          <Box 
            sx={{ 
              mt: 3, 
              p: 3, 
              borderRadius: 2, 
              bgcolor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              textAlign: 'center'
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#166534', 
                fontWeight: 700, 
                mb: 1 
              }}
            >
              🎉 Profil complété !
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ color: '#16a34a' }}
            >
              Félicitations ! Votre profil est maintenant complet et optimisé pour attirer plus de clients.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileInfoModal;
